import React from 'react';
import { inject, observer } from 'mobx-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import withLocalization from '~/hoc/withLocalization';
import Checkbox from '~/components/CustomCheckbox/CustomCheckbox';
import { AUTO_TIMESHEET_DAY_KEYS } from '~/utils/constants';

// Monday-first weekday keys for the auto_timesheet_days object (isoWeekday order).
const DAYS = AUTO_TIMESHEET_DAY_KEYS.map(key => ({ key, label: key.charAt(0).toUpperCase() + key.slice(1) }));

// A day is enabled only when explicitly true; a null/undefined object means all days off.
const isDayEnabled = (autoTimesheetDays, key) => !!autoTimesheetDays && autoTimesheetDays[key] === true;

/**
 * AutoTimeRecordingDays — weekday selector for the auto_timesheet_days object
 * (true = auto time recording ON for that weekday). Self-gated by
 * loginRules.allowShiftToEnableDisableAutoTimesheet (renders nothing when off) -
 * unless isFutureEntryPicker is set, since future entries force auto time recording
 * on regardless of that rule.
 *
 * Props:
 *  - autoTimesheetDays {object|null}
 *  - onChange(nextObject) — receives a full 7-key object
 *  - selectedUsers [{ value, allow_user_for_auto_timesheet }] — profile-based lock/warnings
 *  - disabledDayKeys [string] — weekday keys to grey out and force off (e.g. non-working days)
 *  - forcedOnDayKeys [string] — weekday keys to grey out but force ON (e.g. future schedule entries)
 *  - label, tooltipText — override the default "Auto time recording" text
 *  - labelClassName — extra class on the label+info wrapper (e.g. a fixed min-width for alignment)
 *  - isFutureEntryPicker — skips the allowShiftToEnableDisableAutoTimesheet gate (future entries only need auto-schedule mode)
 *  - disabled — read only: every day keeps showing its value but can't be changed
 */
const AutoTimeRecordingDays = (props) => {
    const {
        t, commonStore, autoTimesheetDays, onChange, selectedUsers = [], disabledDayKeys = [], forcedOnDayKeys = [],
        label = 'Auto time recording',
        tooltipText = 'When enabled, a timesheet will be automatically created based on the shift time',
        labelClassName = '',
        isFutureEntryPicker = false,
        disabled = false,
    } = props;

    const clientData = commonStore && commonStore.config && commonStore.config.client && commonStore.config.client.data;
    const allowShiftAutoTimesheetToggle = !!(clientData && clientData.loginRules && clientData.loginRules.allowShiftToEnableDisableAutoTimesheet);
    const allowUserAutoTimesheetToggle = !!(clientData && clientData.loginRules && clientData.loginRules.allowUserToEnableDisableAutoTimesheet);

    if (!isFutureEntryPicker && !allowShiftAutoTimesheetToggle) return null;

    const disabledProfileUserIds = allowUserAutoTimesheetToggle
        ? selectedUsers.filter(u => u.allow_user_for_auto_timesheet === false).map(u => u.value)
        : [];
    const allAutoTimesheetDisabled = selectedUsers.length > 0 && disabledProfileUserIds.length === selectedUsers.length;
    const someAutoTimesheetDisabled = disabledProfileUserIds.length > 0 && !allAutoTimesheetDisabled;

    // Emit a full 7-key object so the stored value stays explicit.
    const handleDayToggle = (key, checked) => {
        const next = {};
        DAYS.forEach(d => { next[d.key] = isDayEnabled(autoTimesheetDays, d.key); });
        next[key] = checked;
        onChange(next);
    };

    return (
        <div className="mt-2 d-flex align-items-center flex-wrap">
            <div className={`d-flex align-items-center ${labelClassName}`}>
                <label className="control-label fs15 me-2 mb-0">{t(label)}</label>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-auto-time-recording-days">{t(tooltipText)}.</Tooltip>}
                >
                    <i className="fa fa-info-circle text-muted opacity-50 me-3" aria-hidden="true"></i>
                </OverlayTrigger>
            </div>
            <div className="mt-2 d-flex align-items-center flex-wrap">
                {DAYS.map((d) => (
                    <div key={d.key} className="me-2">
                        <Checkbox
                            isChecked={forcedOnDayKeys.includes(d.key) || (!disabledDayKeys.includes(d.key) && isDayEnabled(autoTimesheetDays, d.key))}
                            onChange={(checked) => handleDayToggle(d.key, checked)}
                            label={t(d.label)}
                            disabled={disabled || disabledDayKeys.includes(d.key) || forcedOnDayKeys.includes(d.key) || allAutoTimesheetDisabled}
                        />
                    </div>
                ))}
                {allAutoTimesheetDisabled && (
                    <small className="text-warning ms-2">
                        <i className="fa fa-exclamation-triangle me-1" aria-hidden="true"></i>
                        {t('Auto time recording is disabled for all selected employees via their profile')}
                    </small>
                )}
                {someAutoTimesheetDisabled && (
                    <small className="text-warning ms-2">
                        <i className="fa fa-exclamation-triangle me-1" aria-hidden="true"></i>
                        {`${disabledProfileUserIds.length} ${t('of')} ${selectedUsers.length} ${t('selected employees have auto time recording disabled in their profile — this setting will only apply to the others')}`}
                    </small>
                )}
            </div>
        </div>
    );
};

export default inject('commonStore')(withLocalization(observer(AutoTimeRecordingDays)));
